import { useEffect, useState } from "react";

/**
 * Gates a reveal on a given image (and web fonts) actually being ready, so
 * slow connections see a skeleton instead of a half-painted section. Races
 * against a timeout so a stalled asset can't block the reveal forever.
 */
export default function useAssetReady(imageSrc: string, timeoutMs = 2500): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const imageLoaded = new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = imageSrc;
    });

    const fontsReady = document.fonts?.ready?.then(() => undefined) ?? Promise.resolve();
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));

    Promise.race([Promise.all([imageLoaded, fontsReady]), timeout]).then(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [imageSrc, timeoutMs]);

  return ready;
}
